
import {
    CreateAlbumRequest,
    InfoGraphicsResponse,
    LoginResponse,
    PublicationsResponse,
    RadioIllustrationsResponse,
    Article,
    ArticleSection,
    Paginated, ArticleDTO, toClientArticle,
    AlbumWithPhotos,
} from "../types";
import { API_BASE_URL, API_PATHS } from "./config";
import store from "../store";

// -------------------- small helpers --------------------

/** Your backend expects RAW token (no 'Bearer ') in Authorization */
const getRawAuthHeader = (): Record<string, string> => {
    const raw = store.getState().auth.token;
    if (!raw) return {};
    const token = raw.replace(/^Bearer\s+/i, "").replace(/^"+|"+$/g, "").trim();
    return { Authorization: token };
};

type RequestInitWithHeaders = Omit<RequestInit, "method" | "body"> & {
    headers?: Record<string, string>;
};

const getJson = async <T>(
    url: string,
    init: RequestInitWithHeaders = {}
): Promise<T> => {
    const res = await fetch(url, {
        method: "GET",
        headers: { ...(init.headers || {}) },
        ...init,
    });
    if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
    return res.json();
};

const postJson = async <T>(
    url: string,
    body: unknown,
    init: RequestInitWithHeaders = {}
): Promise<T> => {
    const jsonBody = JSON.stringify(body);

    const fetchOptions: RequestInit = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(init.headers || {})
        },
        body: jsonBody,
        // Remove explicit CORS mode - let browser handle same-origin requests naturally
        // mode: 'cors',  // Removed - causes unnecessary OPTIONS requests for same-origin
        credentials: 'same-origin',  // Changed from 'omit' to match backend CORS config
        ...init,
    };

    const res = await fetch(url, fetchOptions);

    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }
    return res.json();
};

const putJson = async <T>(
    url: string,
    body: unknown,
    init: RequestInitWithHeaders = {}
): Promise<T> => {
    const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(init.headers || {}) },
        body: JSON.stringify(body),
        ...init,
    });

    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }
    return res.json();
};

// For your existing list endpoints
const makeApiCall = async <T>(url: string): Promise<T> => getJson<T>(url);

// -------------------- Albums / existing endpoints --------------------

export const getInfoGraphics = async (
    page: number,
    pageSize: number
): Promise<InfoGraphicsResponse> => {
    return makeApiCall(
        `${API_BASE_URL}${API_PATHS.albums.all}?page=${page}&pageSize=${pageSize}`
    );
};

export const getPublications = async (
    page: number,
    pageSize: number
): Promise<PublicationsResponse> => {
    return makeApiCall(
        `${API_BASE_URL}${API_PATHS.albums.all}?page=${page}&pageSize=${pageSize}`
    );
};

export const getRadioIllustrations = async (
    page: number,
    pageSize: number
): Promise<RadioIllustrationsResponse> => {
    return makeApiCall(
        `${API_BASE_URL}${API_PATHS.albums.all}?page=${page}&pageSize=${pageSize}`
    );
};

/**
 * Get album by ID with photos included
 */
export const getAlbumById = async (albumId: number): Promise<AlbumWithPhotos> => {
    return makeApiCall<AlbumWithPhotos>(
        `${API_BASE_URL}${API_PATHS.albums.byId}/${albumId}`
    );
};

export const getLogin = (email: string, password: string): Promise<LoginResponse> => {
    return postJson<LoginResponse>(`${API_BASE_URL}${API_PATHS.login}`, { email, password });
};

export const verifyGoogleAuth = (credential: string): Promise<any> => {
    return postJson<any>(`${API_BASE_URL}${API_PATHS.auth.google}`, { credential });
};

export const logoutGoogleAuth = (): Promise<any> => {
    return postJson<any>(`${API_BASE_URL}${API_PATHS.auth.logout}`, {}, { credentials: "include" });
};

export const setAlbum = (
    title: string,
    description: string,
    coverImage: string,
    uploadedBy: string,
    category: string
): Promise<CreateAlbumRequest> => {
    const payload = { title, description, coverImage, uploadedBy, category };
    return postJson<CreateAlbumRequest>(`${API_BASE_URL}${API_PATHS.albums.create}`, payload, {
        headers: { ...getRawAuthHeader() },
    });
};

/**
 * Upload a photo to an album.
 * Your config currently has: API_PATHS.photos = "photos/upload?albumId=" (no leading slash)
 * So we build: `${API_BASE_URL}/${API_PATHS.photos}${albumId}`
 */
export async function uploadAlbumPhoto(
    albumId: string,
    file: File,
    imagedesc: string
): Promise<{ photoId: string; message?: string }> {
    console.log("uploadAlbumPhoto called with albumId:", albumId, "Type:", typeof albumId);
    const url = `${API_BASE_URL}${API_PATHS.photos.upload}?albumId=${encodeURIComponent(albumId)}`;
    console.log("Constructed URL:", url);

    const token = store.getState().auth.token?.trim() ?? "";
    const headers: Record<string, string> = token ? { Authorization: token } : {};

    const form = new FormData();
    form.append("image", file);
    form.append("imagedesc", imagedesc);

    const res = await fetch(url, { method: "POST", headers, body: form });
    if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
    return res.json();
}

// -------------------- Articles --------------------

/** Create draft article (slug is generated server-side) */
/** Create draft article (slug is generated server-side) */
// ApiCalls.ts
export const createArticle = (
    title: string,
    excerpt?: string,
    coverImage?: string,
    tags?: string[]
) =>
    postJson<{ article: ArticleDTO }>(
        `${API_BASE_URL}${API_PATHS.articles.base}`,
        {
            title,
            excerpt,
            coverImage: coverImage || undefined, // <-- map here
            tags,
        },
        { headers: { ...getRawAuthHeader() } }
    ).then(({ article }) => ({ article: toClientArticle(article) }));
/** List published (public) */
export const getPublishedArticles = (page = 1, pageSize = 10) =>
    getJson<Paginated<Article>>(
        `${API_BASE_URL}${API_PATHS.articles.base}?page=${page}&pageSize=${pageSize}`
    );

/** List drafts (admin) */
export const getDraftArticles = (page = 1, pageSize = 10) =>
    getJson<Paginated<Article>>(
        `${API_BASE_URL}${API_PATHS.articles.drafts}?page=${page}&pageSize=${pageSize}`,
        { headers: { ...getRawAuthHeader() } }
    );

/** Get article by slug (public) - includes sections */
export const getArticleBySlug = async (slug: string): Promise<{ article: Article & { sections?: ArticleSection[] } }> => {
    const response = await getJson<{ article: ArticleDTO & { sections?: ArticleSection[] } }>(
        `${API_BASE_URL}${API_PATHS.articles.bySlug(slug)}`
    );
    // Convert DTO to client Article, preserving sections
    return {
        article: {
            ...toClientArticle(response.article),
            sections: response.article.sections || []
        }
    };
};

/** Get article by ID (admin) - includes sections and drafts */
export const getArticleById = async (id: number): Promise<{ article: Article & { sections?: ArticleSection[] } }> => {
    const response = await getJson<{ article: ArticleDTO & { sections?: ArticleSection[] } }>(
        `${API_BASE_URL}${API_PATHS.articles.adminById(id)}`,
        { headers: { ...getRawAuthHeader() } }
    );
    // Convert DTO to client Article, preserving sections
    return {
        article: {
            ...toClientArticle(response.article),
            sections: response.article.sections || []
        }
    };
};

/** Delete article */
export const deleteArticle = async (id: number): Promise<{ message: string }> => {
    const url = `${API_BASE_URL}${API_PATHS.articles.delete(id)}`;
    const authHeader = getRawAuthHeader();

    // Debug: Check if token exists
    if (!authHeader.Authorization) {
        console.error('❌ No token found in Redux store');
        throw new Error("Authentication required: Please log in again");
    }

    console.log('🗑️ Deleting article:', id, 'with token:', authHeader.Authorization.substring(0, 20) + '...');

    const res = await fetch(url, {
        method: "DELETE",
        headers: { ...authHeader },
    });

    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error('❌ Delete failed:', res.status, errorText);
        throw new Error(errorText || `HTTP ${res.status}`);
    }
    return res.json();
};

/** Delete album */
export const deleteAlbum = async (id: number): Promise<{ message: string }> => {
    const url = `${API_BASE_URL}${API_PATHS.albums.delete(id)}`;
    const res = await fetch(url, {
        method: "DELETE",
        headers: { ...getRawAuthHeader() },
    });
    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }
    return res.json();
};

/** Like/Unlike album */
export const likeAlbum = async (id: number, action: 'like' | 'unlike' = 'like'): Promise<{ likes: number }> => {
    const url = `${API_BASE_URL}${API_PATHS.albums.like(id)}`;
    const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
        credentials: "include"
    });
    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }
    return res.json();
};

/** Update sections */
export const updateArticleSections = (id: number, sections: ArticleSection[]) =>
    putJson<{ success: boolean }>(
        `${API_BASE_URL}${API_PATHS.articles.sections(id)}`,
        { sections },
        { headers: { ...getRawAuthHeader() } }
    );

/** Publish / unpublish */
export const setArticlePublish = (id: number, publish: boolean) => {
    console.log(`Frontend: Publishing article ${id}, publish: ${publish}`);
    const url = `${API_BASE_URL}${API_PATHS.articles.publish(id)}`;
    console.log(`Frontend: URL: ${url}`);
    return postJson<{ article: Article }>(
        url,
        { publish },
        { headers: { ...getRawAuthHeader() } }
    );
};

/** Upload article cover image to S3 */
export const uploadArticleCoverImage = async (file: File): Promise<string> => {
    // Determine base URL: use direct localhost for development, API_BASE_URL for production
    let baseUrl = API_BASE_URL;
    if (API_BASE_URL === '/api' && typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        // Development: use direct backend URL
        baseUrl = 'http://localhost:3307';
    }
    const url = `${baseUrl}${API_PATHS.articles.uploadCover}`;
    const token = store.getState().auth.token?.trim() ?? "";

    if (!token) {
        throw new Error("Authentication required");
    }

    const form = new FormData();
    form.append("coverImage", file);

    const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: token },
        body: form,
    });

    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }

    const data = await res.json();
    return data.coverImageUrl;
};

/** Upload article figure image to S3 */
export const uploadArticleImage = async (file: File): Promise<string> => {
    // Determine base URL: use direct localhost for development, API_BASE_URL for production
    let baseUrl = API_BASE_URL;
    if (API_BASE_URL === '/api' && typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        // Development: use direct backend URL
        baseUrl = 'http://localhost:3307';
    }
    const url = `${baseUrl}${API_PATHS.articles.uploadFigure}`;
    const token = store.getState().auth.token?.trim() ?? "";

    if (!token) {
        throw new Error("Authentication required");
    }

    const form = new FormData();
    form.append("figureImage", file);

    const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: token },
        body: form,
    });

    if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `HTTP ${res.status}`);
    }

    const data = await res.json();
    return data.imageUrl;
};

// -------------------- Contact Us --------------------

export interface ContactMessage {
    id: string;
    email: string;
    message: string;
    createdAt: string;
}

export const sendContactMessage = (email: string, message: string) => {
    return postJson<ContactMessage>(
        `${API_BASE_URL}${API_PATHS.contact.send}`,
        { email, message }
    );
};

export const getContactMessages = () => {
    return getJson<ContactMessage[]>(
        `${API_BASE_URL}${API_PATHS.contact.list}`
    );
};